/**
 * Client i18n: locale resolution (?lang → saved preference → navigator.languages → en), lazy
 * loading of locale catalogues, and a `useT()` hook. English is always bundled as the fallback.
 */
import { BRAND } from "@shared/brand";
import {
  DEFAULT_LOCALE,
  formatMessage,
  type LocaleTag,
  localeMeta,
  type MessageParams,
  negotiateLocale,
} from "@shared/i18n";
import type { ComponentChildren } from "preact";
import { createContext } from "preact";
import { useContext, useEffect, useMemo, useState } from "preact/hooks";
import { en } from "./locales/en";
import { getPrefs, setPrefs } from "./prefs";

export type Catalog = Record<string, string>;
export type TFunction = (key: string, params?: MessageParams) => string;

const loaders = import.meta.glob<{ default: Catalog }>("./locales/*.ts");
const cache = new Map<string, Catalog>([["en", en]]);

export async function loadCatalog(locale: string): Promise<Catalog> {
  const hit = cache.get(locale);
  if (hit) return hit;
  const loader = loaders[`./locales/${locale}.ts`];
  if (!loader) return en;
  try {
    const mod = await loader();
    cache.set(locale, mod.default);
    return mod.default;
  } catch {
    return en;
  }
}

export function resolveInitialLocale(): LocaleTag {
  const fromQuery = new URLSearchParams(location.search).get("lang");
  if (fromQuery) return negotiateLocale([fromQuery]);
  const saved = getPrefs().locale;
  if (saved) return negotiateLocale([saved]);
  return negotiateLocale(navigator.languages ?? [navigator.language ?? DEFAULT_LOCALE]);
}

interface I18nValue {
  locale: LocaleTag;
  dir: "ltr" | "rtl";
  t: TFunction;
  setLocale: (l: LocaleTag) => void;
  ready: boolean;
}

const I18nContext = createContext<I18nValue>({
  locale: "en",
  dir: "ltr",
  t: (k) => k,
  setLocale: () => undefined,
  ready: true,
});

export function I18nProvider({ children }: { children: ComponentChildren }) {
  const [locale, setLocaleState] = useState<LocaleTag>(() => resolveInitialLocale());
  const [catalog, setCatalog] = useState<Catalog>(() => cache.get(locale) ?? en);
  const [ready, setReady] = useState(cache.has(locale));

  useEffect(() => {
    let cancelled = false;
    setReady(cache.has(locale));
    loadCatalog(locale).then((c) => {
      if (cancelled) return;
      setCatalog(c);
      setReady(true);
    });
    const meta = localeMeta(locale);
    document.documentElement.lang = locale;
    document.documentElement.dir = meta.dir;
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const value = useMemo<I18nValue>(() => {
    // `{brand}` is available in every message without the caller passing it, so no locale file
    // carries the product's name as a literal (pnpm brand:audit enforces that).
    const t: TFunction = (key, params) => {
      const msg = catalog[key] ?? en[key];
      if (msg === undefined) return key;
      return params
        ? formatMessage(locale, msg, { brand: BRAND.name, ...params })
        : msg.includes("{")
          ? formatMessage(locale, msg, { brand: BRAND.name })
          : msg;
    };
    return {
      locale,
      dir: localeMeta(locale).dir,
      t,
      ready,
      setLocale: (l) => {
        setPrefs({ locale: l });
        setLocaleState(l);
      },
    };
  }, [catalog, locale, ready]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  return useContext(I18nContext);
}

export function useT(): TFunction {
  return useContext(I18nContext).t;
}
